<template>
  <div class="staff-payouts-overview">
    <div class="staff-payouts-overview__inner">
      <div class="staff-payouts-overview__filters">
        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Status</h5>
          <md-field>
            <label for="status">Status</label>
            <md-select
              name="status"
              @md-selected="refresh"
              v-model="payouts.filters.statuses"
              multiple
            >
              <md-option
                :value="status.value"
                :key="status.value"
                v-for="status in Payout.options.PAYOUT_STATUS_CHOICES"
              >
                {{ status.label }}
              </md-option>
            </md-select>

            <div
              v-if="hasArrayFilter('statuses')"
              @click="clearArrayFilter('statuses')"
              class="icon-container"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Organization</h5>
          <md-field>
            <label for="organization">Organization</label>
            <md-select
              name="organization"
              @md-selected="refresh"
              v-model="payouts.filters.organizations"
              multiple
            >
              <md-option :value="org.id" :key="org.id" v-for="org in organizations.list">
                {{ org.name }}
              </md-option>
            </md-select>
            <div
              v-if="hasArrayFilter('organizations')"
              @click="clearArrayFilter('organizations')"
              class="icon-container"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
      </div>
      <div class="staff-payouts-overview__table">
        <TableHeader class="table-header" :title="'Payouts'" :collection="payouts">
          <md-button
            v-if="!noPayoutsSelected"
            style="align-self: center; margin-left: 10rem; background-color: black; color: white"
            class="md-primary"
            @click="onBatchPayout"
            :disabled="doingBatchProcess || payouts.refreshing"
          >
            <LoadSpinner v-if="doingBatchProcess" />
            <i v-else>Disburse Selected</i>
          </md-button>
          <md-button
            v-if="!noPayoutsSelected"
            style="align-self: center; margin-left: 2rem"
            @click="onBatchCancel"
            :disabled="doingBatchProcess || payouts.refreshing"
          >
            <LoadSpinner v-if="doingBatchProcess" />
            <i v-else>Cancel Selected</i>
          </md-button>
        </TableHeader>
        <LoadSpinner v-if="payouts.refreshing" style="padding: 2rem 0" />

        <div class="table-wrapper" v-if="!payouts.refreshing">
          <md-table>
            <md-table-row class="header-row">
              <md-table-head>
                <span v-if="Object.keys(selectablePayouts).length" @click="toggleSelectAll">
                  <md-icon v-if="areAllSelectablePayoutsSelected"> check_box </md-icon>
                  <md-icon v-else> check_box_outline_blank </md-icon>
                </span>
                <span v-else>
                  <md-icon :style="{ opacity: 0 }"> check_box_outline_blank </md-icon>
                </span>
              </md-table-head>
              <md-table-head>Name</md-table-head>
              <md-table-head>Organization</md-table-head>
              <md-table-head>Payout Amount</md-table-head>
              <md-table-head>Payout Type</md-table-head>
              <md-table-head>Total Disbursed for Request</md-table-head>
              <md-table-head>Received since last limit reset</md-table-head>
              <md-table-head>Date</md-table-head>
              <md-table-head>Status</md-table-head>
            </md-table-row>

            <md-table-row
              class="body-row"
              v-for="payout in payouts.list"
              :key="payout.id"
              :class="{ 'gray-row': payout.employeeInactive }"
              @click="() => onRowClick(payout)"
            >
              <md-table-cell>
                <span
                  v-if="selectablePayouts[payout.id]"
                  @click.stop="togglePayoutSelected(payout)"
                >
                  <md-icon v-if="selectedPayouts[payout.id]"> check_box </md-icon>
                  <md-icon v-else> check_box_outline_blank </md-icon>
                </span>
                <span v-else>
                  <md-icon :style="{ opacity: 0 }"> check_box_outline_blank </md-icon>
                </span>
              </md-table-cell>
              <md-table-cell>{{ payout.employeeRef.fullName }}</md-table-cell>
              <md-table-cell>{{ payout.organizationName }}</md-table-cell>
              <md-table-cell>{{ payout.amount | currency }}</md-table-cell>
              <md-table-cell>{{ payout.payoutType }}</md-table-cell>
              <md-table-cell>{{ payout.requestApprovedFunds | currency }}</md-table-cell>
              <md-table-cell>{{ payout.employeeReceivedSinceReset | currency }}</md-table-cell>
              <md-table-cell>{{ payout.disbursementTimestamp | formatDateLong }}</md-table-cell>
              <md-table-cell>{{ payout.status }}</md-table-cell>
            </md-table-row>
          </md-table>
        </div>
      </div>
    </div>

    <Modal v-if="modal.showCreated" :width="50" dimmed @close-modal="closeModal">
      <div class="form-container card-shape modal-window">
        <h3 class="modal-header">Summary</h3>
        <p class="modal-summary-para">
          <span class="count">{{ disbursedPayouts.length }}</span> <translate>'Created'</translate>
          {{ disbursedPayouts.length == 1 ? $gettext('Payout') : $gettext('Payouts') }} Sent
        </p>
        <p class="modal-summary-para" v-if="successfulOnRetry.length">
          <span class="count">{{ successfulOnRetry.length }}</span> <translate>Updated</translate>
          {{ successfulOnRetry.length == 1 ? $gettext('Payout') : $gettext('Payouts') }} Sent
        </p>
        <p class="modal-summary-para" v-if="canceledOnRetry.length">
          <span class="count">{{ canceledOnRetry.length }}</span> <translate>Canceled</translate>
          {{ canceledOnRetry.length == 1 ? $gettext('Payout') : $gettext('Payouts') }}
        </p>
        <template v-if="concerningPayouts.length">
          <p class="modal-summary-para">
            <span class="count">{{ concerningPayouts.length }}</span>
            <translate>Concerning</translate>
            {{ concerningPayouts.length == 1 ? $gettext('Status') : $gettext('Statuses') }}
            {{ concerningPayouts.length ? $gettext('- Edit PayPal email and Retry') : null }}
          </p>
          <hr :style="{ margin: '2rem' }" />
          <form
            class="modal-form"
            v-for="payoutFormData in concerningPayouts"
            :key="payoutFormData.id"
            @submit.prevent="onSaveAndRetry(payoutFormData)"
          >
            <span class="employee-name">{{ payoutFormData.employeeName }}</span>
            <input placeholder="PayPal Email" v-model="payoutFormData.paypalEmail" v-translate />
            <md-button
              type="submit"
              :disabled="payoutFormData.loading"
              style="background-color: black; color: white"
            >
              <LoadSpinner v-if="payoutFormData.loading" />
              <i v-else>Save and Retry</i>
            </md-button>
            <md-button
              :disabled="payoutFormData.loading"
              @click="onSinglePayoutCancel(payoutFormData)"
            >
              <LoadSpinner v-if="payoutFormData.loading" />
              <i v-else>Cancel</i>
            </md-button>
          </form>
        </template>
        <md-button :style="{ display: 'block', marginTop: '1rem' }" @click="closeModal"
          >Close</md-button
        >
      </div>
    </Modal>

    <Modal
      v-if="modal.showRejected"
      :width="50"
      dimmed
      @close-modal="closeModal"
    >
      <div class="card-shape modal-window">
        <h2 class="modal-header">Payout(s) Exceed Grant Limit</h2>
        <p class="modal-summary-para">
          The following payouts would exceed grant limits set by the user's organization. The payouts
          have not been processed, and payouts that would exceed grant limits have been canceled.
        </p>

        <md-table style="margin-bottom: 1.5em">
          <md-table-row class="header-row">
            <md-table-head>Name</md-table-head>
            <md-table-head>Organization</md-table-head>
            <md-table-head>Payout Amount</md-table-head>
          </md-table-row>

          <md-table-row
            class="body-row"
            v-for="payout in canceledPayouts"
            :key="payout.id"
            :class="{ 'gray-row': payout.employeeInactive }"
          >
            <md-table-cell>{{ payout.employeeRef.fullName }}</md-table-cell>
            <md-table-cell>{{ payout.requestRef.organizationName }}</md-table-cell>
            <md-table-cell>{{ payout.amount | currency }}</md-table-cell>
          </md-table-row>
        </md-table>

        <div class="button-container">
          <button class="form-button" @click="closeModal"><span v-translate>Close</span></button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { LoadSpinner, TableHeader } from '@/components'
import Modal from '@/components/Modal'

import Payout from '@/services/payouts'
import Employee from '@/services/employees'
import Organization from '@/services/organizations'
import SelectOrganization from '@/services/selectOrganizations'
import CollectionManager from '@/services/collectionManager'
import { objectToCamelCase } from '@/services/utils'
import { toMessageString } from '@/services/api'
import Pagination from '@/services/pagination'

export default {
  name: 'PayoutsOverview',
  components: {
    LoadSpinner,
    TableHeader,
    Modal,
  },
  data: function() {
    return {
      Payout,
      doingBatchProcess: false,
      payouts: CollectionManager.create({
        ModelClass: Payout,
        filters: {
          ordering: '-datetime_created',
          statuses: [],
          organizations: [],
        },
      }),
      selectedPayouts: {},
      concerningPayouts: [],
      disbursedPayouts: [],
      canceledPayouts: [],
      successfulOnRetry: [],
      canceledOnRetry: [],
      modal: {
        showCreated: false,
        showRejected: false,
      },
      organizations: CollectionManager.create({
        ModelClass: SelectOrganization,
        pagination: Pagination.create({ pageSize: 40 }),
      }),
    }
  },
  created() {
    this.refresh()
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    isStaff() {
      return this.user.isStaff
    },
    noPayoutsSelected() {
      return Object.keys(this.selectedPayouts).length == 0
    },
    areAllSelectablePayoutsSelected() {
      return Object.keys(this.selectedPayouts).length == Object.keys(this.selectablePayouts).length
    },
    selectablePayouts() {
      let selectableStatuses = ['created', 'denied', 'failed', 'unclaimed']
      let reducer = (accumulator, currentPayout) => {
        let status = currentPayout.status.toLowerCase()
        let isSelectable = selectableStatuses.includes(status)
        if (isSelectable && !currentPayout.employeeInactive) {
          accumulator[currentPayout.id] = currentPayout
        }
        return accumulator
      }

      return this.payouts.list.reduce(reducer, {})
    },
  },
  methods: {
    refresh() {
      this.payouts.refresh()
      this.organizations.refresh()
    },
    onRowClick(payout) {
      if(payout.organizationIsTcr) {
        this.$router.push({
          name: 'StaffStipendRequestDetail',
          params: {
            id: payout.requestId,
          },
        })
        return
      }

      this.$router.push({
        name: 'StaffRequestDetail',
        params: {
          id: payout.requestId,
        },
      })
    },
    togglePayoutSelected(payout) {
      if (this.selectedPayouts[payout.id]) {
        let newData = { ...this.selectedPayouts }
        delete newData[payout.id]
        this.selectedPayouts = newData
      } else {
        this.selectedPayouts = { ...this.selectedPayouts, [payout.id]: payout }
      }
    },
    toggleSelectAll() {
      if (this.areAllSelectablePayoutsSelected) {
        this.selectedPayouts = {}
      } else {
        this.selectedPayouts = { ...this.selectablePayouts }
      }
    },
    async onBatchPayout() {
      let confirmation = confirm('Are you sure you wish to disburse all selected Payouts?')
      if (!confirmation) {
        return
      }
      this.doingBatchProcess = true
      this.$store.state.invalidateAxiosApi = true

      // Some of the selectedPayouts are able to be disbursed without modification (those whose status is 'Created')
      // The others need to go through a modification(s) and so are a part of other/subsequent UX
      // For those that need modification, relevant data is gathered to populate a v-bound form
      let toBeDisbursed = []
      let needModification = []
      for (let selectedPayout of Object.values(this.selectedPayouts)) {
        let status = selectedPayout.status.toLowerCase()
        if (status == 'created') {
          toBeDisbursed.push(selectedPayout.id)
        } else {
          let forForm = {
            id: selectedPayout.id,
            employeeID: selectedPayout.employeeRef.id,
            employeeName: selectedPayout.employeeRef.fullName,
            paypalEmail: selectedPayout.employeeRef.paypalEmail,
            /* NOTE: for validation purposes (the newly entered email must be different than the original email ) */
            initialPaypalEmail: selectedPayout.employeeRef.paypalEmail,
            loading: false,
          }
          needModification.push(forForm)
        }
      }

      const selectedPayoutPayoutTypes = Object.values(this.selectedPayouts).map(
        payout => payout.payoutType,
      )
      const uniquePayoutTypes = new Set(selectedPayoutPayoutTypes)

      if (uniquePayoutTypes.size > 1) {
        this.$Alert.alert({
          type: 'error',
          message: `
            <h4>Can't disburse payouts of different types.</h4>
            <p>Please only select payouts of the same payout type to disburse.</p>
          `,
        })
        this.refresh()
        this.doingBatchProcess = false
        return
      }

      if (uniquePayoutTypes.size === 1 && toBeDisbursed.length) {
        await Payout.api
          .createBatchPayout(toBeDisbursed, selectedPayoutPayoutTypes[0])
          .then(() => {
            this.$Alert.alert({
              type: 'success',
              message: `
                <h4>Disbursement Sent</h4>
                <p>Selected 'Created' payouts have been transitioned to the 'Pending' status.</p>
              `,
            })
            this.concerningPayouts = needModification
            this.disbursedPayouts = toBeDisbursed
            this.modal.showCreated = true
            this.refresh()
          })
          .catch(err => {
            const response = err.response

            if (response.status === 412) {
              this.canceledPayouts = response.data.map(p => objectToCamelCase(p))
              this.modal.showRejected = true
            } else {
              const { data } = response
              const message = toMessageString(data)

              this.$Alert.alert({
                type: 'error',
                message: `<p>${message}</p>`,
                timeout: 6000,
              })
            }

            this.refresh()
          })
          .finally(() => {
            this.doingBatchProcess = false
          })
      }
    },
    async onSaveAndRetry(payoutFormData) {
      let confirmation = confirm(
        "Are you sure you wish to update this user's PayPal email and retry disbursement of the selected Payout?",
      )
      if (!confirmation) {
        return
      }
      // Part 1: update the employee's paypal email
      payoutFormData.loading = true
      let isBlank = !payoutFormData.paypalEmail
      let isSameAsInitial = payoutFormData.paypalEmail == payoutFormData.initialPaypalEmail
      if (isBlank || isSameAsInitial) {
        payoutFormData.loading = false
        this.$Alert.alert({
          type: 'error',
          message: `
              <h4>Error Updating Email</h4>
              <p>Enter a new & valid email.</p>
            `,
        })
        return
      }
      let emailUpdateSuccess = false
      const employee = Employee.create({
        id: payoutFormData.employeeID,
        paypalEmail: payoutFormData.paypalEmail,
      })
      const updateFields = ['paypalEmail']
      await Employee.api
        .update(employee, updateFields)
        .then(() => {
          emailUpdateSuccess = true
        })
        .catch(() => {
          this.$Alert.alert({
            type: 'error',
            message: `
              <h4>Error Updating Email</h4>
              <p>Enter a new & valid email.</p>
            `,
          })
        })
      if (!emailUpdateSuccess) {
        payoutFormData.loading = false
        return
      }

      // Part 2: if email-update was successful, produce a new BatchPayout with only this request in the batch
      await Payout.api
        .createBatchPayout([payoutFormData.id], payoutFormData.payoutType)
        .then(() => {
          this.$Alert.alert({
            type: 'success',
            message: `
              <h4>Disbursement Sent</h4>
              <p>Payout has been transitioned to the 'Pending' status.</p>
            `,
          })
          this.refresh()
        })
        .finally(() => {
          payoutFormData.loading = false
        })

      // update Modal summary to incude this success
      // remove modal form for successfully retried payout
      this.successfulOnRetry.push(payoutFormData.id)
      this.concerningPayouts = this.concerningPayouts.filter(
        payout => payout.id !== payoutFormData.id,
      )
    },
    onBatchCancel() {
      let confirmation = confirm(
        'Are you sure you wish to transition selected Payouts to the "Canceled" status?',
      )
      if (!confirmation) {
        return
      }
      let toBeCanceled = Object.keys(this.selectedPayouts)
      this.cancelPayouts(toBeCanceled)
    },
    async onSinglePayoutCancel(payoutFormData) {
      let confirmation = confirm(
        'Are you sure you wish to transition selected Payout to the "Canceled" status?',
      )
      if (!confirmation) {
        return
      }
      let toBeCanceled = [payoutFormData.id]
      payoutFormData.loading = true
      await this.cancelPayouts(toBeCanceled)
      payoutFormData.loading = false

      // update Modal summary to incude this success
      // remove modal form for successfully canceled payout
      this.canceledOnRetry.push(payoutFormData.id)
      this.concerningPayouts = this.concerningPayouts.filter(
        payout => payout.id !== payoutFormData.id,
      )
    },
    cancelPayouts(toBeCanceled) {
      this.doingBatchProcess = true
      return Payout.api
        .batchCancel(toBeCanceled)
        .then(() => {
          this.$Alert.alert({
            type: 'success',
            message: `
                <h4>Updates Successful</h4>
                <p>Selected Payouts have been transitioned to the 'Canceled' status.</p>
              `,
          })
          this.refresh()
        })
        .finally(() => {
          this.doingBatchProcess = false
          this.resetSelectionsData()
        })
    },
    closeModal() {
      // close modal, reset component state regarding selections and modal
      // any success-related updates to this.payouts will have already taken place via this.refresh()
      let newState = {
        modal: {
          showCreated: false,
          showRejected: false,
        },
      }
      Object.assign(this.$data, newState)
      this.resetSelectionsData()
    },
    resetSelectionsData() {
      let newState = {
        selectedPayouts: {},
        concerningPayouts: [],
        disbursedPayouts: [],
        successfulOnRetry: [],
        canceledOnRetry: [],
        rejectedPayouts: [],
      }
      Object.assign(this.$data, newState)
    },
    hasArrayFilter(filterKey) {
      let container = this.payouts.filters[filterKey]
      return container ? container.length > 0 : false
    },
    clearArrayFilter(filterKey) {
      this.payouts.filters[filterKey] = []
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.staff-payouts-overview {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-max-width: 112rem);

  &__inner {
    max-width: 100%;
  }

  @media (min-width: $mobile-break-point) {
    &__inner {
      display: flex;
    }

    &__filters {
      flex: 1;
      padding: 1rem 2rem 0 1rem;
      min-width: 16rem;
    }

    &__table {
      flex: 8;
      overflow-x: auto;
    }
  }
}

// Table Styles
.table-wrapper {
  max-width: 100%;
  overflow-x: auto;
}

.table-header {
  height: 4rem;
}

.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 0.9rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem 0;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

.gray-row {
  background-color: $mid-gray;

  &:hover {
    background-color: $gray;
  }
}

// Modal Styles
.modal-header {
  margin-bottom: 2rem;
}

.modal-summary-para {
  margin-bottom: 1rem;

  .count {
    font-size: 1rem;
    margin-right: 3rem;
  }
}

.modal-form {
  display: flex;
  flex-flow: row;
  align-items: center;

  .employee-name {
    font-size: 0.8rem;
    width: 14rem;
  }

  input {
    width: 15rem;
    height: 2.25rem;
    padding: 0 0.5rem;
  }

  button {
    margin-left: auto;
    padding: 0.1rem 1rem;
  }
}

// Filter UI

.FilterAspect {
  margin-bottom: 32px;
}

.FilterAspect__Title {
  margin-bottom: 0;
}

.FilterAspect__Clear {
  color: gray;
}
</style>
